import {
  SEARCH_GENDER_KEY,
  SEARCH_LANGUAGE_KEY,
  SEARCH_RICHNESS_KEY,
  SEARCH_SORT_KEY,
} from "../_components/Constants"

export interface SearchFilters {
  /** 排序: 'chat_num'(热门) | 'update_time'(最近) | undefined(全部) */
  sort?: string
  /** 性别: 1男 2女 3非二元 (多选) */
  gender: number[]
  /** 角色丰富度: 1非常详细 2详细 3普通 4简单 (多选) */
  info_token: number[]
  /** 角色原始语言 (多选) */
  language: string[]
}

export const SORT_OPTIONS = [
  { value: "", labelKey: "all" },
  { value: "chat_num", labelKey: "popular" },
  { value: "update_time", labelKey: "recently" },
] as const

export const RICHNESS_OPTIONS = [
  { value: 1, labelKey: "veryDetailed" },
  { value: 2, labelKey: "detailed" },
  { value: 3, labelKey: "ordinary" },
  { value: 4, labelKey: "simple" },
] as const

export const GENDER_OPTIONS = [
  { value: 1, labelKey: "male" },
  { value: 2, labelKey: "female" },
  { value: 3, labelKey: "neutral" },
] as const

const parseNumberArray = (val?: string | null): number[] => {
  if (!val) return []
  return val
    .split(",")
    .map((item) => Number(item))
    .filter((item) => !Number.isNaN(item))
}

const parseStringArray = (val?: string | null): string[] => {
  if (!val) return []
  return val.split(",").filter(Boolean)
}

/** 从 searchParams (对象 或 URLSearchParams) 中解析筛选条件 */
export const parseSearchFilters = (
  searchParams: Record<string, any> | URLSearchParams,
): SearchFilters => {
  const get = (key: string): string | undefined | null => {
    if (searchParams instanceof URLSearchParams) return searchParams.get(key)
    return searchParams?.[key]
  }

  const sort = get(SEARCH_SORT_KEY) || undefined

  return {
    sort: sort && sort !== "1" ? sort : undefined,
    gender: parseNumberArray(get(SEARCH_GENDER_KEY)),
    info_token: parseNumberArray(get(SEARCH_RICHNESS_KEY)),
    language: parseStringArray(get(SEARCH_LANGUAGE_KEY)),
  }
}

/** 生成稳定的 queryKey 片段，保证客户端与服务端 hydrate 一致 */
export const filtersToKey = (filters: SearchFilters): string => {
  return [
    filters.sort || "",
    [...filters.gender].sort((a, b) => a - b).join("|"),
    [...filters.info_token].sort((a, b) => a - b).join("|"),
    [...filters.language].sort().join("|"),
  ].join("__")
}

/** 转为接口请求参数（仅包含非空字段，对齐 app 行为） */
export const filtersToRequestParams = (filters: SearchFilters) => {
  const params: {
    sort?: string
    gender?: number[]
    info_token?: number[]
    language?: string[]
  } = {}
  if (filters.sort) params.sort = filters.sort
  if (filters.gender.length) params.gender = filters.gender
  if (filters.info_token.length) params.info_token = filters.info_token
  if (filters.language.length) params.language = filters.language
  return params
}

/** 当前激活的筛选项数量（不含分类） */
export const countActiveFilters = (filters: SearchFilters): number => {
  return (
    (filters.sort ? 1 : 0) +
    filters.gender.length +
    filters.info_token.length +
    filters.language.length
  )
}

export const EMPTY_FILTERS: SearchFilters = {
  sort: undefined,
  gender: [],
  info_token: [],
  language: [],
}

/** 不可变地切换数组中的某一项（多选场景复用） */
export const toggleInArray = <T>(arr: T[], value: T): T[] =>
  arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]

/** 把筛选条件写回 URLSearchParams（与 parseSearchFilters 互为逆操作，保持单一数据源） */
export const applyFiltersToSearchParams = (
  sp: URLSearchParams,
  filters: SearchFilters,
): URLSearchParams => {
  const setOrDel = (key: string, val: string) => {
    if (val) sp.set(key, val)
    else sp.delete(key)
  }
  setOrDel(SEARCH_SORT_KEY, filters.sort || '')
  setOrDel(SEARCH_GENDER_KEY, filters.gender.join(','))
  setOrDel(SEARCH_RICHNESS_KEY, filters.info_token.join(','))
  setOrDel(SEARCH_LANGUAGE_KEY, filters.language.join(','))
  return sp
}
